import type { Group } from 'types/group'

/**
 * Moves multiple items to a new position within an array (legacy implementation).
 *
 * Note: This function has known bugs and should be replaced with moveItems2.
 * Moves specified items by their indices to a target position.
 *
 * @param items - The array of items to manipulate
 * @param movingItems - Array of indices of items to move
 * @param targetPosition - The target position to move items to
 * @returns New array with items moved to the target position
 *
 * @deprecated Use moveItems2 instead for more reliable behavior
 *
 * @example
 * ```typescript
 * const items = ['a', 'b', 'c', 'd', 'e']
 * const result = moveItems(items, [1, 3], 0)
 * // Moves items at indices 1 and 3 to position 0
 * ```
 */
export const moveItems = (
  items: any[],
  movingItems: number[],
  targetPosition: number,
): any[] => {
  const itemsWithChangedPosition = movingItems.map(index => {
    return items[index]
  })

  let correctedInsertedPosition = targetPosition
  movingItems.forEach(index => {
    if (index < targetPosition) correctedInsertedPosition--
  })

  const filteredItems = items.filter((item, index) => {
    return movingItems.indexOf(index) === -1
  })

  return [
    ...filteredItems.slice(0, correctedInsertedPosition),
    ...itemsWithChangedPosition,
    ...filteredItems.slice(correctedInsertedPosition),
  ]
}

/**
 * Normalizes array indices by filtering out invalid indices.
 *
 * Ensures all indices are within valid bounds (0 to array.length - 1).
 *
 * @param array - The array to validate indices against
 * @param indexes - Array of indices to normalize
 * @returns Array of valid indices within bounds
 *
 * @example
 * ```typescript
 * const arr = ['a', 'b', 'c']
 * normalizeIndexes(arr, [-1, 0, 2, 5]) // returns [0, 2]
 * ```
 */
function normalizeIndexes<T>(array: T[], indexes: number[]): number[] {
  return indexes.filter(index => index >= 0 && index < array.length)
}

/**
 * Normalizes the target index for move operations.
 *
 * Ensures the target index is within valid bounds considering the number
 * of items being moved.
 *
 * @param array - The array being manipulated
 * @param fromIndexes - Array of source indices being moved
 * @param toIndex - Target index to normalize
 * @returns Normalized target index within valid bounds
 *
 * @example
 * ```typescript
 * const arr = ['a', 'b', 'c', 'd']
 * normalizeToIndex(arr, [1, 2], 10) // returns 2 (max valid position)
 * ```
 */
function normalizeToIndex<T>(
  array: T[],
  fromIndexes: number[],
  toIndex: number,
): number {
  return Math.min(Math.max(0, toIndex), array.length - fromIndexes.length)
}

/**
 * Moves items within an array to a new position (improved implementation).
 *
 * More reliable replacement for moveItems with proper index normalization
 * and bounds checking. Handles both single items and multiple items.
 *
 * @param array - The array to manipulate
 * @param from - Index or array of indices of items to move
 * @param to - Target position to move items to
 * @returns New array with items moved to the target position
 *
 * @example
 * ```typescript
 * const items = ['a', 'b', 'c', 'd', 'e']
 *
 * // Move single item
 * const result1 = moveItems2(items, 1, 3)
 * // ['a', 'c', 'd', 'b', 'e'] - moved 'b' to position 3
 *
 * // Move multiple items
 * const result2 = moveItems2(items, [1, 3], 0)
 * // ['b', 'd', 'a', 'c', 'e'] - moved 'b' and 'd' to start
 * ```
 */
export function moveItems2<T>(
  array: T[],
  from: number | number[],
  to: number,
): T[] {
  const indexes = Array.isArray(from) ? from : [from]
  const normalizedIndexes = normalizeIndexes(array, indexes)
  const normalizedToIndex = normalizeToIndex(array, indexes, to)

  const movedItems = normalizedIndexes
    .map(index => array[index])
    .filter((item): item is T => item !== undefined)

  const result = array.filter(
    (item, index) => normalizedIndexes.indexOf(index) === -1,
  )

  result.splice(normalizedToIndex, 0, ...movedItems)

  return result
}

/**
 * Updates an item at a specific index in an array.
 *
 * Creates a new array with the item at the specified index replaced
 * with the provided update.
 *
 * @param items - The array to update
 * @param index - Index of the item to update
 * @param update - New item to replace the existing item
 * @returns New array with the item updated
 *
 * @example
 * ```typescript
 * const items = ['a', 'b', 'c']
 * const updated = updateItem(items, 1, 'B')
 * // ['a', 'B', 'c'] - replaced 'b' with 'B'
 * ```
 */
export function updateItem<Item>(
  items: Item[],
  index: number,
  update: Item,
): Item[] {
  return [...items.slice(0, index), update, ...items.slice(index + 1)]
}

/**
 * Adds an item at a specific index in an array.
 *
 * Creates a new array with the item inserted at the specified position,
 * shifting existing items to the right.
 *
 * @param items - The array to add to
 * @param index - Position to insert the new item
 * @param item - Item to insert
 * @returns New array with the item added
 *
 * @example
 * ```typescript
 * const items = ['a', 'c']
 * const updated = addItem(items, 1, 'b')
 * // ['a', 'b', 'c'] - inserted 'b' at position 1
 * ```
 */
export function addItem<Item>(
  items: readonly Item[],
  index: number,
  item: Item,
): Item[] {
  const result = [...items]
  result.splice(index, 0, item)
  return result
}

/**
 * Adds multiple items at a specific index in an array.
 *
 * Creates a new array with multiple items inserted at the specified position,
 * shifting existing items to the right.
 *
 * @param items - The array to add to
 * @param index - Position to insert the new items
 * @param newItems - Array of items to insert
 * @returns New array with the items added
 *
 * @example
 * ```typescript
 * const items = ['a', 'd']
 * const updated = addItems(items, 1, ['b', 'c'])
 * // ['a', 'b', 'c', 'd'] - inserted 'b' and 'c' at position 1
 * ```
 */
export function addItems<Item>(
  items: Item[],
  index: number,
  newItems: Item[],
): Item[] {
  const result = [...items]
  result.splice(index, 0, ...newItems)
  return result
}

type Dictionary<K extends string | number | symbol, V> = Partial<Record<K, V>>

/**
 * Updates a single key in a dictionary object.
 *
 * Creates a new dictionary with the specified key updated to the new value.
 *
 * @param dictionary - The dictionary to update
 * @param key - The key to update
 * @param update - The new value for the key
 * @returns New dictionary with the key updated
 *
 * @example
 * ```typescript
 * const dict = { a: 1, b: 2 }
 * const updated = updateKey(dict, 'b', 5)
 * // { a: 1, b: 5 } - updated 'b' from 2 to 5
 * ```
 */
export function updateKey<
  D extends object,
  K extends PropertyKey,
  V extends K extends keyof D ? D[K] : D[keyof D],
>(dictionary: D, key: K, update: V): Omit<D, K> & Record<K, V> {
  return {
    ...dictionary,
    [key]: update,
  } as Omit<D, K> & Record<K, V>
}

/**
 * Applies a set of changes to specific keys within a dictionary object.
 *
 * @param dictionary - The original dictionary to apply changes to
 * @param keys - An array of keys within the dictionary that the changes should be applied to
 * @param changeSet - An object representing the changes to be applied. This object
 *                   should have the same shape as the dictionary values but can be a subset
 * @returns A new dictionary object with the changes applied to the specified keys
 * @template K, V
 *
 * @example
 * ```typescript
 * const users = {
 *   1: { name: 'John', active: true, role: 'user' },
 *   2: { name: 'Jane', active: false, role: 'admin' },
 *   3: { name: 'Bob', active: true, role: 'user' }
 * }
 *
 * const updated = applyChangeSet(users, [1, 3], { active: false })
 * // Updates users 1 and 3 to set active: false
 * // {
 * //   1: { name: 'John', active: false, role: 'user' },
 * //   2: { name: 'Jane', active: false, role: 'admin' },
 * //   3: { name: 'Bob', active: false, role: 'user' }
 * // }
 * ```
 */
export function applyChangeSet<K extends string | number | symbol, V>(
  dictionary: Dictionary<K, V>,
  keys: K[],
  changeSet: Partial<V>,
): Record<K, V> {
  const update: Partial<Record<K, V>> = {}
  keys.forEach(key => {
    const existingValue = dictionary[key]
    if (existingValue !== undefined) {
      update[key] = {
        ...existingValue,
        ...changeSet,
      } as V
    }
  })
  // safe: update only contains keys already in dictionary (guarded above), so the spread preserves the full shape
  return { ...dictionary, ...update } as Record<K, V>
}

/**
 * Removes an item at a specific index from an array.
 *
 * Creates a new array with the item at the specified index removed.
 *
 * @param items - The array to remove from
 * @param index - Index of the item to remove
 * @returns New array with the item removed
 *
 * @example
 * ```typescript
 * const items = ['a', 'b', 'c']
 * const updated = deleteItem(items, 1)
 * // ['a', 'c'] - removed 'b' at index 1
 * ```
 */
export const deleteItem = (items: any[], index: number): any[] => [
  ...items.slice(0, index),
  ...items.slice(index + 1),
]

/**
 * Removes a group from all entities that reference it.
 *
 * Helper function for GROUP_DELETE_SUCCESS action. Iterates through
 * all entities and removes the specified group from their groups array.
 *
 * @param state - Dictionary of entities with groups arrays
 * @param group - The group to remove from all entities
 * @returns New state with the group removed from all entities
 *
 * @example
 * ```typescript
 * const state = {
 *   track1: { groups: ['group1', 'group2'] },
 *   track2: { groups: ['group2'] }
 * }
 * const group = { url: 'group2' }
 * const updated = removeGroupFromEntities(state, group)
 * // {
 * //   track1: { groups: ['group1'] },
 * //   track2: { groups: [] }
 * // }
 * ```
 */
export function removeGroupFromEntities<
  K extends string | number | symbol,
  T extends { groups: string[] },
  S extends Record<K, T>,
>(state: S, group: Group): S {
  // helper for GROUP_DELETE_SUCCESS
  const result: S = { ...state } as S
  Object.keys(state).forEach(prop => {
    const item = state[prop as K]
    if (item !== undefined) {
      const index = item.groups.indexOf(group.url)
      if (index !== -1) {
        result[prop as K] = {
          ...item,
          groups: [
            ...item.groups.slice(0, index),
            ...item.groups.slice(index + 1),
          ],
          // S[K] is T by constraint but TS can't prove it without the intermediate T cast
        } as T as S[K]
      }
    }
  })
  return result
}
